import React from "react";
import { FiAlertCircle, FiCheckCircle, FiInfo, FiX } from "react-icons/fi";

import {
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  Text,
  UseToastOptions,
} from "@chakra-ui/react";

type ToastStatus = "success" | "error" | "warning" | "info";

const toastStyleByStatus: Record<ToastStatus, {
  borderColor: string;
  borderTopColor: string;
  iconColor: string;
  statusIcon: typeof FiCheckCircle;
}> = {
  success: {
    borderColor: "green.200",
    borderTopColor: "green.400",
    iconColor: "green.500",
    statusIcon: FiCheckCircle,
  },
  error: {
    borderColor: "red.200",
    borderTopColor: "red.400",
    iconColor: "red.500",
    statusIcon: FiAlertCircle,
  },
  warning: {
    borderColor: "orange.200",
    borderTopColor: "orange.400",
    iconColor: "orange.500",
    statusIcon: FiInfo,
  },
  info: {
    borderColor: "blue.200",
    borderTopColor: "blue.400",
    iconColor: "blue.500",
    statusIcon: FiInfo,
  },
};

type ToastInvoker = (options?: UseToastOptions) => string | number | undefined;

interface ToastOptions {
  description?: string;
  status?: ToastStatus;
  title: string;
}

export const showToast = (
  toast: ToastInvoker,
  {
    description,
    status = "success",
    title,
  }: ToastOptions,
) => {
  const { borderColor, borderTopColor, iconColor, statusIcon } = toastStyleByStatus[status];

  return toast({
    position: "top-right",
    duration: 3000,
    containerStyle: {
      marginTop: "72px",
    },
    render: ({ onClose }) => (
      <Flex
        bg="white"
        borderRadius="12px"
        boxShadow="lg"
        border="1px solid"
        borderColor={borderColor}
        borderTopWidth="3px"
        borderTopColor={borderTopColor}
        p={3}
        minW="290px"
        align="flex-start"
        justify="space-between"
        gap={3}
      >
        <HStack align="flex-start" spacing={2}>
          <Icon as={statusIcon} color={iconColor} boxSize={5} mt="2px" />
          <Box>
            <Text fontWeight="700" fontSize="14px" color="gray.800" mb={description ? 1 : 0}>
              {title}
            </Text>
            {description ? (
              <Text fontSize="12px" color="gray.600" m={0}>
                {description}
              </Text>
            ) : null}
          </Box>
        </HStack>

        <IconButton
          aria-label="close-toast"
          icon={<FiX />}
          size="xs"
          variant="ghost"
          color="gray.500"
          onClick={onClose}
        />
      </Flex>
    ),
  });
};
